import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OnboardingProgress from './OnboardingProgress';
import TermsViewer from './TermsViewer';
import PasswordCreation from './PasswordCreation';
import TwoFactorSetup from './TwoFactorSetup';
import { useToast } from '@/hooks/use-toast';
import type { OnboardingStep, OnboardingStepProps } from './types';

interface StepConfig {
  id: OnboardingStep;
  title: string;
  description: string;
  component: React.ComponentType<OnboardingStepProps>;
}

const TOS_CONTENT = `Last Updated May 8, 2025

Welcome to VITL! These Terms of Use ("Terms") govern your access to and use of this website ("Site"). These Terms are a binding agreement between you ("you" or the "user") and VITLrx, Inc. ("VITL," "we," "our," or "us"). By accessing or using the Site, you affirm your consent to these Terms.

PLEASE READ THESE TERMS CAREFULLY BEFORE USING THE SITE. YOUR USE OF THE SITE IS DEEMED AN ACCEPTANCE OF THESE TERMS. IF YOU DO NOT AGREE WITH THESE TERMS, PLEASE DO NOT USE THE SITE.

1. About the Services
The Services allow users to:
• Create and manage relationships with pharmacies.
• Utilize our Platform to track fulfillment statuses, communicate with reps, and maintain care plan documentation.

2. Ownership and Intellectual Property Rights
VITL is the sole owner of our name, logo, and trademarks, and all associated intellectual property rights. VITL or our licensors own all content and materials published on the Site ("Site Content") and all associated intellectual property rights.

3. No Medical Advice
Our Site Content is not intended to be used or interpreted as medical advice, and you should not rely on the timeliness, accuracy, or veracity of the Site Content.

4. Warranty Disclaimers
THE SITE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTY OF ANY KIND. YOUR USE OF THE SITE, SITE CONTENT, PLATFORM, AND SERVICES IS AT YOUR SOLE RISK.

5. Contact Information
VITL Support: 903-884-8579
Email: support@vitlrx.com`;

const BAA_CONTENT = `Last Updated May 8, 2025

This Business Associate Agreement ("BAA") is entered into between VITLrx, Inc. ("VITL," "we," "our," or "us") and you ("Business Associate") to ensure compliance with the Health Insurance Portability and Accountability Act of 1996 ("HIPAA") and its implementing regulations.

BY ACCEPTING THIS AGREEMENT, YOU ACKNOWLEDGE THAT YOU UNDERSTAND AND AGREE TO COMPLY WITH ALL HIPAA REQUIREMENTS AS A BUSINESS ASSOCIATE OF VITL.

1. Definitions
For purposes of this BAA, the terms "Covered Entity," "Designated Record Set," "Disclosure," "Health Care Operations," "Individual," "Minimum Necessary," "Notice of Privacy Practices," "Protected Health Information," "Required By Law," "Secretary," "Security Incident," "Subcontractor," "Unsecured Protected Health Information," and "Use" shall have the same meaning as those terms in the HIPAA Rules.

2. Permitted Uses and Disclosures
Business Associate may use or disclose Protected Health Information only as permitted or required under this BAA or as Required By Law. Business Associate may use or disclose Protected Health Information for the proper management and administration of Business Associate or to carry out the legal responsibilities of Business Associate.

3. Prohibited Uses and Disclosures
Business Associate shall not use or disclose Protected Health Information other than as permitted or required by this BAA or as Required By Law. Business Associate shall not use or disclose Protected Health Information in a manner that would violate Subpart E of 45 CFR Part 164 if done by Covered Entity.

4. Safeguards
Business Associate shall use appropriate safeguards, and comply with Subpart C of 45 CFR Part 164 with respect to electronic Protected Health Information, to prevent use or disclosure of Protected Health Information other than as provided for by this BAA.

5. Reporting
Business Associate shall report to Covered Entity any use or disclosure of Protected Health Information not provided for by this BAA of which it becomes aware, including breaches of unsecured Protected Health Information as required at 45 CFR 164.410, and any Security Incident of which it becomes aware.

6. Contact Information
VITL Support: 903-884-8579
Email: support@vitlrx.com`;

const EULA_CONTENT = `Last Updated May 8, 2025

This End User License Agreement ("EULA") is a legal agreement between you ("End User" or "you") and VITLrx, Inc. ("VITL," "we," "our," or "us") for the use of VITL's software platform and services.

BY ACCEPTING THIS AGREEMENT, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THE TERMS AND CONDITIONS OF THIS EULA.

1. License Grant
Subject to the terms of this EULA, VITL grants you a limited, non-exclusive, non-transferable license to use the VITL software platform solely for your internal business purposes in accordance with the documentation provided by VITL.

2. Restrictions
You may not: (a) reverse engineer, decompile, or disassemble the software; (b) modify, adapt, or create derivative works based on the software; (c) distribute, sell, lease, or sublicense the software; or (d) use the software for any unlawful purpose.

3. Data Protection
You acknowledge that the software may process personal and health information. You agree to comply with all applicable data protection laws and regulations, including HIPAA, in your use of the software.

4. Support and Updates
VITL may, but is not obligated to, provide technical support and software updates. Any updates provided will be subject to the terms of this EULA unless accompanied by a separate license agreement.

5. Termination
This license is effective until terminated. Your rights under this license will terminate automatically without notice if you fail to comply with any term of this EULA. Upon termination, you must cease all use of the software and destroy all copies.

6. Limitation of Liability
TO THE MAXIMUM EXTENT PERMITTED BY LAW, VITL SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SOFTWARE.

7. Contact Information
VITL Support: 903-884-8579
Email: support@vitlrx.com`;

const STEPS: StepConfig[] = [
  {
    id: 'tos',
    title: 'Terms of Service',
    description: 'Please review and accept our Terms of Service to continue your registration',
    component: (props: OnboardingStepProps) => (
      <TermsViewer
        {...props}
        title="Terms of Service"
        content={TOS_CONTENT}
        acceptLabel="I have read and accept the Terms of Service"
      />
    ),
  },
  {
    id: 'baa',
    title: 'Business Associate Agreement',
    description: 'Please review and accept our Business Associate Agreement to continue your registration',
    component: (props: OnboardingStepProps) => (
      <TermsViewer
        {...props}
        title="Business Associate Agreement"
        content={BAA_CONTENT}
        acceptLabel="I have read and accept the Business Associate Agreement"
      />
    ),
  },
  {
    id: 'eula',
    title: 'End User License Agreement',
    description: 'Please review and accept our End User License Agreement to complete your registration',
    component: (props: OnboardingStepProps) => (
      <TermsViewer
        {...props}
        title="End User License Agreement"
        content={EULA_CONTENT}
        acceptLabel="I have read and accept the End User License Agreement"
      />
    ),
  },
  {
    id: 'password',
    title: 'Create Password',
    description: 'Create a secure password for your VITL account',
    component: (props: OnboardingStepProps) => <PasswordCreation {...props} />,
  },
  {
    id: '2fa',
    title: 'Two-Factor Authentication',
    description: 'Secure your account with two-factor authentication',
    component: (props: OnboardingStepProps) => <TwoFactorSetup {...props} />,
  },
];

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  
  const currentStepParam = searchParams.get('step') as OnboardingStep | null;
  const currentStepIndex = currentStepParam 
    ? STEPS.findIndex(s => s.id === currentStepParam)
    : 0;

  const [stepIndex, setStepIndex] = useState(
    currentStepIndex >= 0 ? currentStepIndex : 0
  );

  const currentStep = STEPS[stepIndex] || STEPS[0];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === STEPS.length - 1;

  useEffect(() => {
    // Update URL when step changes
    setSearchParams({ step: STEPS[stepIndex].id });
    
    // Scroll to top when step changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stepIndex, setSearchParams]);

  // Safety check - if stepIndex is out of bounds, reset to first step
  useEffect(() => {
    if (stepIndex < 0 || stepIndex >= STEPS.length) {
      setStepIndex(0);
    }
  }, [stepIndex]);

  const handleNext = () => {
    if (isLastStep) {
      toast({
        title: "Account Setup Complete!",
        description: "Welcome to VITL! You can now access all features.",
      });
      navigate('/dashboard');
    } else {
      setStepIndex(stepIndex + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setStepIndex(stepIndex - 1);
    }
  };

  const CurrentStepComponent = currentStep.component;

  return (
    <>
   
      
      {/* Fixed progress bar at top */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm pt-6 pb-4">
        <OnboardingProgress 
          steps={STEPS.map(s => s.title)}
          currentStep={stepIndex}
        />
      </div>
      
      <div className="bg-gray-50 min-h-screen">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-vitl-green mb-4">
                {currentStep.title}
              </h1>
              <p className="text-xl text-gray-600">
                {currentStep.description}
              </p>
            </div>
            
            <CurrentStepComponent
              onNext={handleNext}
              onBack={handleBack}
              isFirstStep={isFirstStep}
              isLastStep={isLastStep}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingWizard;